import { State, Operation, merkle, Synced, ClockService, DocumentType } from '@icc/b2b/shared/worker';
import { sendMessage } from './worker-message.model';
import { Subject } from 'rxjs';
import { idbService } from './worker-db';

const limits = {
    retries: 3,
    socketAckTimeout: 30000,
};

export let workerState: State = getDefaultState();

let connecting = false;
let running = false;

let browserOpenedDocuments: string[] = [];
let openedDocuments: string[] = [];
const openedProcessingResults$ = new Subject<{
    graphs: Record<string, merkle.MerkleTrie>;
    updatedGraphs: Record<string, number>;
    key: number;
}>();

export { browserOpenedDocuments, openedDocuments, running, connecting, openedProcessingResults$, limits };

export function setConnecting(value: boolean) {
    connecting = value;
}

export function setRunning(value: boolean) {
    running = value;
}

export async function setOpenedDocuments(documents: string[]) {
    const normalizedDocuments = Array.from(new Set(documents));
    const changed = normalizedDocuments.length !== browserOpenedDocuments.length
        || browserOpenedDocuments.some((docId) => !normalizedDocuments.includes(docId))
        || normalizedDocuments.some((docId) => !browserOpenedDocuments.includes(docId));
    browserOpenedDocuments = normalizedDocuments;
    openedDocuments = [...browserOpenedDocuments];
    if (openedDocuments.length) {
        await idbService.setOpenedDocuments(normalizedDocuments);
    }
    ClockService.deletedDocs = ClockService.deletedDocs.filter(docId => !openedDocuments.includes(docId));
    return changed
}

export async function setConnectedOpenedDocuments(documentConnected: Record<string, string[]>) {
    const nextOpenedDocuments = [...browserOpenedDocuments];
    for (const documentId of nextOpenedDocuments) {
        if (documentConnected[documentId]) {
            const connectedDocuments = documentConnected[documentId];
            for (const connectedDocument of connectedDocuments) {
                if (!nextOpenedDocuments.includes(connectedDocument)) {
                    nextOpenedDocuments.push(connectedDocument);
                }
            }
        }
    }
    openedDocuments = nextOpenedDocuments;
    if (openedDocuments.length) {
        await idbService.setOpenedDocuments(openedDocuments);
    }
    ClockService.deletedDocs = ClockService.deletedDocs.filter(docId => !openedDocuments.includes(docId));
}

export function updateSyncedState(
    syncedDocuments: string[],
    diffMap: Map<string, number>,
    init = false,
) {
    const changedSynced = new Map<string, boolean>();
    for (const documentId of syncedDocuments) {
        if (!diffMap.has(documentId) || diffMap.get(documentId) !== 0) {
            if (workerState.synced.get(documentId) !== Synced.Syncing) {
                changedSynced.set(documentId, true);
            }
            workerState.synced.set(documentId, Synced.Syncing);
        }
    }

    for (const documentId in ClockService.getClock().merklesMap) {
        if (ClockService.getClock().merklesMap[documentId]?.hasAdd) {
            if (diffMap.has(documentId) && diffMap.get(documentId) === 0) {
                if (workerState.synced.get(documentId) !== Synced.Synced) {
                    changedSynced.set(documentId, true);
                }
                workerState.synced.set(documentId, Synced.Synced);
            } else if (workerState.synced.get(documentId) !== Synced.Syncing) {
                if (
                    !workerState.opsToSend.some(
                        (op) =>
                            op.docId === documentId || ('buyDocId' in op && op.buyDocId === documentId)
                    )
                ) {
                    if (workerState.synced.get(documentId) !== Synced.Download) {
                        changedSynced.set(documentId, true);
                    }
                    workerState.synced.set(documentId, Synced.Download);
                }
            }
        } else {
            if (workerState.synced.get(documentId) !== Synced.Download) {
                changedSynced.set(documentId, true);
            }
            workerState.synced.set(documentId, Synced.Download);
        }
    }
    for (const documentId in ClockService.serverGraphs) {
        if (!workerState.synced.has(documentId)
            && ClockService.serverGraphs[documentId]?.hasAdd) {
            if (!ClockService.localLastOpened[documentId] && !ClockService.serverLastModified[documentId] && !openedDocuments.includes(documentId)) {
                if (workerState.synced.get(documentId) !== Synced.Online) {
                    changedSynced.set(documentId, true);
                }
                workerState.synced.set(documentId, Synced.Online);
            } else {
                if (workerState.synced.get(documentId) !== Synced.Download) {
                    changedSynced.set(documentId, true);
                }
                workerState.synced.set(documentId, Synced.Download);
            }
        }
    }
    if (changedSynced.size || init) {
        sendMessage({
            type: 'updatedSyncedState',
            data: {
                synced: workerState.synced,
                changedDocs: Array.from(changedSynced.keys()),
            }
        });
    }
}

function getDefaultState(): State {
    return {
        loaded: false,
        documents: {},
        documentsIds: [],
        documentTypes: new Map<string, DocumentType>(),
        positions: {},
        appointments: {},
        complaintNotes: {},
        complaintSpareParts: {},
        attachments: [],
        documentConnections: {},
        deliveries: {},
        operations: [],
        operationsKeysMap: new Map<string, Operation>(),
        operationsIds: new Map<string, true>(),
        operationsIdByDocs: new Map<string, Map<string, true>>(),
        opsToSendToWorker: [],
        opsToSend: [],
        opsToSave: [],
        staleDocs: new Map<string, number>(),
        userId: 0,
        userCompanyId: 0,
        userMarketId: 0,
        userEmail: '',
        userPhoneNumber: '',
        userReferenceName: '',
        synchronizationInProgress: false,
        changesOnServer: 0,
        synced: new Map<string, Synced>(),
        graphsToSend: new Map<string, number>(),
        savedTags: new Map<string, boolean>(),
    };
}

export function resetState() {
    workerState = getDefaultState();
}

export async function cleanState(neededDocsIds: string[]) {
    const newState: State = {
        ...workerState,
        documents: {},
        documentsIds: [],
        documentTypes: new Map<string, DocumentType>(),
        positions: {},
        appointments: {},
        complaintNotes: {},
        complaintSpareParts: {},
        attachments: [],
        documentConnections: {},
        deliveries: {},
        operations: [],
        operationsIds: new Map<string, true>(),
    }
    const neededDocsMap = new Map<string, true>();
    for (const docId of neededDocsIds) {
        neededDocsMap.set(docId, true);
    }
    for (const docId of workerState.documentsIds) {
        if (neededDocsMap.has(docId)) {
            newState.documents[docId] = workerState.documents[docId];
            newState.documentsIds.push(docId);
            newState.documentTypes.set(docId, workerState.documentTypes.get(docId));
            newState.operationsIdByDocs.set(docId, workerState.operationsIdByDocs.get(docId));
        }
    }

    for (const positionId in workerState.positions) {
        if (neededDocsMap.has(workerState.positions[positionId].documentId)) {
            newState.positions[positionId] = workerState.positions[positionId];
        }
    }

    for (const appointmentId in workerState.appointments) {
        if (neededDocsMap.has(workerState.appointments[appointmentId]?.documentId)) {
            newState.appointments[appointmentId] = workerState.appointments[appointmentId];
        }
    }

    for (const noteId in workerState.complaintNotes) {
        if (neededDocsMap.has(workerState.complaintNotes[noteId]?.documentId)) {
            newState.complaintNotes[noteId] = workerState.complaintNotes[noteId];
        }
    }

    for (const sparePartId in workerState.complaintSpareParts) {
        if (neededDocsMap.has(workerState.complaintSpareParts[sparePartId]?.documentId)) {
            newState.complaintSpareParts[sparePartId] = workerState.complaintSpareParts[sparePartId];
        }
    }

    for (const attachment of workerState.attachments) {
        if (neededDocsMap.has(attachment?.documentId)) {
            newState.attachments.push(attachment);
        }
    }

    for (const positionId in workerState.deliveries) {
        if (neededDocsMap.has(workerState.deliveries[positionId].documentId)) {
            newState.deliveries[positionId] = workerState.deliveries[positionId];
        }
    }


    for (const docId in workerState.documentConnections) {
        if (neededDocsMap.has(docId)) {
            newState.documentConnections[docId] = workerState.documentConnections[docId];
        }
    }

    for (const [key, buyDocumentConnections] of Object.entries(workerState.documentConnections)) {
        if (neededDocsMap.has(key)) {
            for (const [buyKey, documentConnection] of Object.entries(buyDocumentConnections)) {
                if (neededDocsMap.has(buyKey)) {
                    if (!newState.documentConnections[key]) {
                        newState.documentConnections[key] = {};
                    }
                    newState.documentConnections[key][buyKey] = documentConnection;
                }
            }
        }
    }

    for (const key in newState.operationsKeysMap) {
        const op = newState.operationsKeysMap[key];
        if (!neededDocsMap.has(op.docId) && !('buyDocId' in op && neededDocsMap.has(op.buyDocId))) {
            delete newState.operationsKeysMap[key];
        }
    }

    for (const op of workerState.operations) {
        if (neededDocsMap.has(op.docId) || ('buyDocId' in op && neededDocsMap.has(op.buyDocId))) {
            newState.operations.push(op);
            newState.operationsIds.set(op.id, true);
        }
    }

    Object.assign(workerState, newState);
}
